/**
 * I segni disegnati sopra la carta, in pixel dello schermo: il segno di un'azienda (alone + anello per livello +
 * nucleo), quello di un gruppo (il numero delle aziende vicine) e i nomi.
 *
 * Ogni segno è un bottone vero (focus da tastiera, Invio e Spazio, nome letto ad alta voce) con un bersaglio di almeno
 * 44 pixel, anche quando il disegno è più piccolo: si tocca col dito.
 */
import React from 'react';
import { alpha } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { ContiPerLivello, LivelloMappa } from '@shared/admin/mappaAziende';
import { tokens } from '../../theme';
import { COLORE_LIVELLO, COLORI_MAPPA } from './aspetto';
import { archiDeiLivelli, raggioNucleo, type Arco } from './segni';

const BERSAGLIO_MINIMO = 22;
/** La tessera del logo al centro del segno: abbastanza grande da riconoscere un marchio, mai più del segno. */
const TESSERA_LOGO = 22;
/** Con il logo, il segno non scende sotto questo raggio: la tessera ci deve stare dentro con un filo d'alone attorno. */
export const RAGGIO_MINIMO_CON_LOGO = 17;
/** Il segno piccolo di una sede secondaria (bordo bianco compreso). */
export const RAGGIO_SEDE = 7.5;

/**
 * Qualcuno ci sta lavorando adesso: un anello verde fermo e, sopra, un'onda che si allarga e svanisce. È l'unico
 * movimento continuo della mappa, perché è l'unica cosa che cambia mentre la si guarda. Senza movimento (preferenza di
 * sistema) resta l'anello fermo.
 */
const AlLavoroAdesso: React.FC<{ raggio: number }> = ({ raggio }) => (
  <g aria-hidden style={{ pointerEvents: 'none' }}>
    <circle className="mappa-segno__onda" r={raggio + 3} fill="none" stroke={COLORI_MAPPA.adesso} strokeWidth={2} />
    <circle r={raggio + 3} fill="none" stroke={COLORI_MAPPA.adesso} strokeWidth={2} />
  </g>
);

const AnelloDeiLivelli: React.FC<{ raggio: number; archi: Arco[]; spessore: number; tratteggio?: boolean; spento?: boolean }> = ({
  raggio, archi, spessore, tratteggio = false, spento = false,
}) => {
  const circonferenza = 2 * Math.PI * raggio;
  const stacco = archi.length > 1 ? 1.5 : 0;
  return (
    <g transform="rotate(-90)">
      {archi.map((a) => (
        <circle
          key={a.livello}
          r={raggio}
          fill="none"
          stroke={spento ? COLORI_MAPPA.spento : COLORE_LIVELLO[a.livello]}
          strokeWidth={spessore}
          strokeDasharray={
            tratteggio
              ? '3 3'
              : archi.length === 1
                ? undefined
                : `${Math.max(0, (a.fine - a.inizio) * circonferenza - stacco)} ${circonferenza}`
          }
          strokeDashoffset={archi.length > 1 && !tratteggio ? -a.inizio * circonferenza : undefined}
        />
      ))}
    </g>
  );
};

function premuto(e: React.KeyboardEvent, azione: () => void) {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    azione();
  }
}

export interface SegnoAziendaProps {
  x: number;
  y: number;
  raggio: number;
  livello: LivelloMappa;
  account: ContiPerLivello;
  accountTotale: number;
  personeAttive: number;
  spenta: boolean;
  selezionata: boolean;
  attenuata: boolean;
  alLavoro: boolean;
  /** Il logo quadrato dell'azienda: sta su una tessera bianca al centro. */
  logo: string | null;
  etichetta: string;
  ritardo: number | null;
  onScegli: () => void;
}

export const SegnoAzienda: React.FC<SegnoAziendaProps> = ({
  x, y, raggio, livello, account, accountTotale, personeAttive, spenta, selezionata, attenuata, alLavoro, logo, etichetta, ritardo, onScegli,
}) => {
  const colore = spenta ? COLORI_MAPPA.spento : COLORE_LIVELLO[livello];
  const [logoRotto, setLogoRotto] = React.useState(false);
  const conLogo = !!logo && !logoRotto;
  // Con il logo il nucleo diventa la cornice colorata attorno alla tessera: c'è appena qualcuno ha lavorato, e si
  // allarga con le persone. Senza nessuno al lavoro resta solo la tessera sull'alone.
  const nucleoPuro = raggioNucleo(raggio, accountTotale, personeAttive);
  const nucleo = conLogo && nucleoPuro > 0 ? Math.max(nucleoPuro, TESSERA_LOGO / 2 + 2.5) : nucleoPuro;
  const pieno = livello !== 'free' && !spenta;
  return (
    <g
      className="mappa-segno"
      transform={`translate(${x} ${y})`}
      role="button"
      tabIndex={0}
      aria-label={etichetta}
      aria-pressed={selezionata}
      onClick={(e) => { e.stopPropagation(); onScegli(); }}
      onKeyDown={(e) => premuto(e, onScegli)}
      style={{ opacity: attenuata ? 0.42 : 1 }}
    >
      <g className={ritardo !== null ? 'mappa-segno__entrata' : undefined} style={ritardo !== null ? { animationDelay: `${ritardo}ms` } : undefined}>
        {alLavoro && <AlLavoroAdesso raggio={raggio} />}
        {selezionata && <circle className="mappa-segno__scelta" r={raggio + (alLavoro ? 8 : 5)} fill="none" stroke={tokens.color.graphite} strokeWidth={1.6} />}
        <circle r={raggio} fill={pieno ? alpha(colore, 0.14) : alpha(COLORI_MAPPA.terra, 0.75)} />
        <AnelloDeiLivelli raggio={raggio - 1} archi={archiDeiLivelli(account, livello)} spessore={2} tratteggio={spenta} spento={spenta} />
        {nucleo > 0 && <circle r={nucleo} fill={spenta ? alpha(colore, 0.6) : colore} />}
        {conLogo && (
          <g style={{ pointerEvents: 'none', opacity: spenta ? 0.55 : 1 }}>
            <rect x={-TESSERA_LOGO / 2} y={-TESSERA_LOGO / 2} width={TESSERA_LOGO} height={TESSERA_LOGO} rx={5} fill={COLORI_MAPPA.terra} stroke={alpha(tokens.color.graphite, 0.12)} strokeWidth={1} />
            <image
              href={logo ?? undefined}
              x={-TESSERA_LOGO / 2 + 2.5}
              y={-TESSERA_LOGO / 2 + 2.5}
              width={TESSERA_LOGO - 5}
              height={TESSERA_LOGO - 5}
              preserveAspectRatio="xMidYMid meet"
              onError={() => setLogoRotto(true)}
            />
          </g>
        )}
      </g>
      <circle className="mappa-segno__bersaglio" r={Math.max(BERSAGLIO_MINIMO, raggio + 4)} fill="transparent" />
    </g>
  );
};

/**
 * Il segno di una sede che non è la principale: un cerchietto tratteggiato del colore del livello, su un bordo bianco
 * che lo stacca dalla carta. Gli account e le persone stanno sul segno della sede principale: qui si dice solo «anche
 * qui lavora questa azienda».
 */
export const SegnoSede: React.FC<{
  x: number;
  y: number;
  livello: LivelloMappa;
  spenta: boolean;
  selezionata: boolean;
  attenuata: boolean;
  etichetta: string;
  onScegli: () => void;
}> = ({ x, y, livello, spenta, selezionata, attenuata, etichetta, onScegli }) => {
  const colore = spenta ? COLORI_MAPPA.spento : COLORE_LIVELLO[livello];
  return (
    <g
      className="mappa-segno"
      transform={`translate(${x} ${y})`}
      role="button"
      tabIndex={0}
      aria-label={etichetta}
      aria-pressed={selezionata}
      onClick={(e) => { e.stopPropagation(); onScegli(); }}
      onKeyDown={(e) => premuto(e, onScegli)}
      style={{ opacity: attenuata ? 0.42 : 1 }}
    >
      {selezionata && <circle className="mappa-segno__scelta" r={10} fill="none" stroke={tokens.color.graphite} strokeWidth={1.6} />}
      {/* Il cerchietto tratteggiato (Simone, 28/10/2026): «anche qui lavora questa azienda», distinto dal segno pieno. */}
      <circle r={RAGGIO_SEDE - 1} fill={COLORI_MAPPA.terra} stroke={COLORI_MAPPA.terra} strokeWidth={3} />
      <circle r={RAGGIO_SEDE - 1.5} fill={alpha(colore, 0.12)} stroke={colore} strokeWidth={1.8} strokeDasharray="2.6 2" />
      <circle className="mappa-segno__bersaglio" r={BERSAGLIO_MINIMO} fill="transparent" />
    </g>
  );
};

export interface SegnoGruppoProps {
  x: number;
  y: number;
  raggio: number;
  quante: number;
  account: ContiPerLivello;
  contieneLaScelta: boolean;
  spento: boolean;
  alLavoro: boolean;
  onScegli: () => void;
}

export const SegnoGruppo: React.FC<SegnoGruppoProps> = ({ x, y, raggio, quante, account, contieneLaScelta, spento, alLavoro, onScegli }) => {
  const { t } = useTranslation('mappa');
  const r = Math.max(raggio, 14);
  return (
    <g
      className="mappa-segno"
      transform={`translate(${x} ${y})`}
      role="button"
      tabIndex={0}
      aria-label={t('mappa.gruppo', { count: quante })}
      onClick={(e) => { e.stopPropagation(); onScegli(); }}
      onKeyDown={(e) => premuto(e, onScegli)}
    >
      {alLavoro && <AlLavoroAdesso raggio={r} />}
      {contieneLaScelta && <circle r={r + (alLavoro ? 8 : 5)} fill="none" stroke={tokens.color.graphite} strokeWidth={1.6} />}
      <circle r={r} fill={COLORI_MAPPA.terra} />
      <AnelloDeiLivelli raggio={r - 1.25} archi={archiDeiLivelli(account, 'base')} spessore={2.5} spento={spento} />
      <circle r={r - 5} fill={alpha(tokens.color.graphite, 0.04)} />
      <text
        textAnchor="middle"
        dominantBaseline="central"
        style={{
          fontFamily: tokens.font.display,
          fontWeight: 600,
          fontSize: Math.min(20, Math.max(13, r * 0.78)),
          fill: tokens.color.graphite,
          fontVariantNumeric: 'tabular-nums',
          pointerEvents: 'none',
        }}
      >
        {quante}
      </text>
      <circle r={Math.max(BERSAGLIO_MINIMO, r + 4)} fill="transparent" />
    </g>
  );
};

/** Un nome sulla carta: quello di un'azienda in inchiostro, quello dei comuni di un gruppo più chiaro. */
export const NomeSullaMappa: React.FC<{ x: number; y: number; ancora: 'start' | 'end'; testo: string; forte: boolean; tono: 'azienda' | 'luogo' }> = ({
  x, y, ancora, testo, forte, tono,
}) => (
  <text
    x={x}
    y={y}
    textAnchor={ancora}
    dominantBaseline="central"
    className="mappa-nome"
    style={{
      fontFamily: tokens.font.body,
      fontWeight: forte ? 700 : 600,
      fontSize: 12.5,
      fill: tono === 'luogo' ? tokens.color.ash : tokens.color.graphite,
      paintOrder: 'stroke',
      stroke: alpha(COLORI_MAPPA.terra, 0.94),
      strokeWidth: 4,
      strokeLinejoin: 'round',
      pointerEvents: 'none',
    }}
  >
    {testo}
  </text>
);

/** Il carattere dei nomi, per misurarli prima di posarli (stesso stile di `NomeSullaMappa`). */
export const CARATTERE_NOMI = `600 12.5px ${tokens.font.body}`;
